import React, { useEffect, useState, useRef } from "react";
import Chart from "chart.js/auto";
import { fmesh } from "../../../core/globalVariables.js";

const PDFPieChart = ({
  geojson,
  selectedMetric,
  selectedYearEditing,
  landKeySelectedKey,
  handleLandtypePieHover,
  PDFpiechartRef
}) => {
  const [timeSeriesArray, setTimeSeriesArray] = useState([]);
  const PDFextendedpiechartRef = useRef(null);
  const canvasRef = useRef(null);

  const calculatePercentages = (geojson, property) => {
    const sumTotal = {};
    let grandTotal = 0;

    geojson.features.forEach((feature) => {
      const propertyValue =
        feature.feature_data_simple_timeseries[property]?.[selectedYearEditing];
      const metric =
        feature.feature_data_simple_timeseries[selectedMetric]?.[
          selectedYearEditing
        ];

      if (
        propertyValue &&
        typeof propertyValue === "string" &&
        metric !== undefined
      ) {
        const roundedMetric = Math.round(metric);
        sumTotal[propertyValue] = (sumTotal[propertyValue] || 0) + roundedMetric;
        grandTotal += roundedMetric;
      }
    });

    if (grandTotal === 0) return {};

    const percentages = {};
    for (const landKey in sumTotal) {
      percentages[landKey] = (
        (sumTotal[landKey] / grandTotal) *
        100
      ).toFixed(2);
    }

    return percentages;
  };

  useEffect(() => {
    const tempArray = [];
    fmesh.traverse((child) => {
      if (child.isMesh) {
        geojson.features.forEach((feature) => {
          if (child.name === feature.skylark_id) {
            const timeSeriesData =
              feature.feature_data_simple_timeseries[landKeySelectedKey]?.[
                selectedYearEditing
              ];
            if (timeSeriesData) {
              const r = Math.round(child.material.color.r * 255);
              const g = Math.round(child.material.color.g * 255);
              const b = Math.round(child.material.color.b * 255);
              const hexColor = `#${(
                (1 << 24) +
                (r << 16) +
                (g << 8) +
                b
              )
                .toString(16)
                .slice(1)
                .toUpperCase()}`;
              const uniqueEntry = { timeSeriesData, color: hexColor };

              if (
                !tempArray.some(
                  (entry) =>
                    entry.timeSeriesData === uniqueEntry.timeSeriesData &&
                    entry.color === uniqueEntry.color
                )
              ) {
                tempArray.push(uniqueEntry);
              }
            }
          }
        });
      }
    });

    setTimeSeriesArray(tempArray);
  }, [geojson, landKeySelectedKey, selectedYearEditing]);

  useEffect(() => {
    if (!geojson || !landKeySelectedKey) return;

    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    // Destroy old chart before creating new
    if (PDFextendedpiechartRef.current) {
      PDFextendedpiechartRef.current.destroy();
    }

    const percentages = calculatePercentages(geojson, landKeySelectedKey);
    const labels = Object.keys(percentages);
    const dataValues = Object.values(percentages);

    const differentiatorColorMap = {};
    timeSeriesArray.forEach((entry) => {
      differentiatorColorMap[entry.timeSeriesData] = entry.color;
    });

    const backgroundColors = labels.map(
      (label) => differentiatorColorMap[label] || "#ccc"
    );

    PDFextendedpiechartRef.current = new Chart(ctx, {
      type: "pie",
      data: {
        labels,
        datasets: [
          {
            data: dataValues,
            backgroundColor: backgroundColors,
            hoverOffset: 4,
          },
        ],
      },
      options: {
        plugins: {
          legend: {
            display: true,
            position: "right",
            align: "start",
            labels: {
              boxWidth: 10,
              padding: 5,
              usePointStyle: true,
              textAlign: "left",
            },
          },
          tooltip: {
            enabled: false,
          },
        },
        animation: false,
        onHover: (event, chartElement) => {
          if (chartElement.length) {
            const index = chartElement[0].index;
            handleLandtypePieHover(labels[index], landKeySelectedKey);
          } else {
            handleLandtypePieHover("Show all");
          }
        },
      },
    });
  }, [geojson, landKeySelectedKey, selectedMetric, selectedYearEditing, timeSeriesArray]);

  return (
    <>
      {geojson && (
        <div
          ref={PDFpiechartRef}
          style={{
            width: "300px",
            height: "300px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              width: "100%",
              height: "100%",
            }}
          ></canvas>
        </div>
      )}
    </>
  );
};

export default PDFPieChart;
